/**
 * The privacy policy (/privacy) and terms of service (/terms). Public: no sign-in needed.
 *
 * Every name, address and jurisdiction comes from the brand (PLAN.md section 1), so a rename or a new
 * legal entity is a config change. What the pages say about data must stay true to the code: if a
 * processor, a stored field or a retention period changes, change the text here in the same commit.
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import { useBranding } from "@/lib/useBranding";

function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  const { brand } = useBranding();
  return (
    <div className="min-h-dvh bg-[#0f1115] text-slate-200">
      <header className="border-b border-white/8 bg-[#16181d]">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" aria-label={`${brand.product_name} home`}>
            <img src={brand.logo_url} alt={brand.product_name} className="h-10 w-auto" />
          </Link>
          <nav className="flex gap-4 text-sm text-slate-400">
            <Link className="hover:text-white" to="/privacy">Privacy</Link>
            <Link className="hover:text-white" to="/terms">Terms</Link>
          </nav>
        </div>
      </header>
      <main
        className="mx-auto max-w-3xl px-4 py-10 leading-relaxed
          [&_a]:text-sky-300 [&_a]:underline [&_a]:underline-offset-2
          [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-white
          [&_li]:mb-1.5 [&_p]:mb-4 [&_strong]:text-white
          [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6"
      >
        <h1 className="text-3xl font-bold text-white">{title}</h1>
        <p className="text-sm text-slate-400">Last updated {brand.terms_version}</p>
        {children}
      </main>
    </div>
  );
}

function Mail({ to }: { to: string }) {
  return <a href={`mailto:${to}`}>{to}</a>;
}

export function PrivacyPage() {
  const { brand } = useBranding();
  const name = brand.product_name;
  const support = <Mail to={brand.support_email} />;
  return (
    <LegalPage title="Privacy Policy">
      <p>
        This policy explains what personal data {name} ({brand.primary_domain}) collects, why, who it is shared
        with, and your rights. We have tried to keep it short and plain.
      </p>

      <h2>Who we are</h2>
      <p>
        {name} is run by {brand.legal_name} (“we”, “us”), the controller of your personal data. For anything about
        your data, email {support}.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Your account.</strong> Your email address and, if you sign in with Google, the name on your Google
          account. We do not receive your Google password.
        </li>
        <li>
          <strong>Sign-in records.</strong> Our sign-in provider records when and how you sign in. If you choose a
          password, it is stored only as a secure hash by that provider, never by us in readable form.
        </li>
        <li>
          <strong>What you put into studies.</strong> The messages, promises and audience choices you enter, and the
          results {name} produces: simulated personas, their reactions and the reports.
        </li>
        <li>
          <strong>Usage and credit.</strong> Which studies you run, how much processing they used, and your credit
          balance and charges.
        </li>
        <li>
          <strong>Security records.</strong> Our hosting keeps request logs, including IP addresses, for up to 30
          days. To stop the sign-in form being used to flood inboxes, we count sign-in emails per address using
          one-way hashes of the email and IP address, and delete those counts after about a day.
        </li>
      </ul>
      <p>
        Please don't enter personal data about other real people in your studies. {name} doesn't need it, and you
        would need a lawful basis to share it.
      </p>

      <h2>Why we use it</h2>
      <ul>
        <li>
          <strong>To provide the service</strong>: to create and secure your account, run your studies, show your
          results and keep your credit balance. This is necessary to perform our contract with you.
        </li>
        <li>
          <strong>To keep {name} safe and working</strong>: to prevent abuse, investigate problems and fix faults.
          This is in our legitimate interests.
        </li>
        <li>
          <strong>To meet legal duties</strong>: for example, keeping billing records for a purchase you make.
        </li>
      </ul>
      <p>
        We send service emails only, such as sign-in links. We do not send marketing email without your consent, we
        do not sell your data, and we do not use it for advertising.
      </p>

      <h2>How AI is involved</h2>
      <p>
        To run a study, we send its content (your message, promises and the audience description) to Anthropic’s
        Claude API, which generates the simulated personas and their reactions. We do not use your content to train
        AI models. Under Anthropic’s commercial terms, content sent through its API is not used to train its models
        by default. Your email address is not sent with study content.
      </p>

      <h2>Who we share it with</h2>
      <p>We use these providers to run {name}, each acting on our instructions:</p>
      <ul>
        <li>
          <strong>Google Cloud and Firebase</strong>: hosting, the database, file storage and sign-in. Your account
          and study data are stored in Google’s Belgium (europe-west1) region.
        </li>
        <li>
          <strong>Anthropic</strong>: generates the personas and reactions in your studies (United States).
        </li>
        <li>
          <strong>Resend</strong>: delivers our emails, sending from its Ireland region.
        </li>
        <li>
          <strong>Stripe</strong>: takes payments for paid plans and top-ups. We never see or store
          your full card number.
        </li>
      </ul>
      <p>
        We may also disclose data if the law requires it, or to protect the rights and safety of users or others.
      </p>

      <h2>International transfers</h2>
      <p>
        Some of these providers are based in, or may process data in, the United States. Where that happens, we rely
        on the safeguards they offer, such as the UK–US data bridge or standard contractual clauses.
      </p>

      <h2>How long we keep it</h2>
      <p>
        We keep your account and study data while your account is open. If you ask us to delete your account, we
        delete your data within 30 days. We keep only what the law requires us to keep for longer, such as billing
        records, which are usually kept for six years. Security logs and sign-in counts are kept as described above.
      </p>
      <p>
        Each plan quotes a retention window &mdash; 30 days on the free plan, a year on paid plans. We are telling
        you plainly how that works today: deleting data past its window is not yet automatic, so in practice we
        keep your studies while your account is open and delete them when you ask. We will not start deleting on
        a schedule without telling you first, and the windows are the shortest we would ever apply. If your plan
        ends, your data stays for the window of the plan you were on before we remove it, so nothing disappears
        the moment a payment lapses.
      </p>

      <h2>Your rights</h2>
      <p>
        You do not have to ask us for a copy of your data: the billing page has a
        <strong> Download everything</strong> button that gives you the whole account as one file &mdash; your
        studies and their versions, every run&rsquo;s results, your audiences and your billing history. Each
        run&rsquo;s results can also be downloaded on their own as a spreadsheet or a report.
      </p>
      <p>
        You can also ask us to correct your data, to delete it, or to restrict or object to how we use it. Email
        {support} and we will reply within one month. If you are unhappy with our answer, you
        can complain to the data protection authority where you live. In the UK, that is the Information
        Commissioner’s Office (<a href="https://ico.org.uk">ico.org.uk</a>).
      </p>

      <h2>Cookies and browser storage</h2>
      <p>
        {name} sets no cookies of its own, and uses no advertising, analytics or tracking cookies. There is no
        consent banner because there is nothing to consent to: everything below is stored in your browser only to
        provide the service you asked for, never to follow you or to build a profile.
      </p>
      <p>What is stored in your browser, and why:</p>
      <ul>
        <li>your sign-in session, so you are not asked to sign in on every page;</li>
        <li>
          your email address between asking for a sign-in link and opening it, so the link can be completed on
          the device that requested it;
        </li>
        <li>a study you are part-way through setting up, so you do not lose it by closing the tab;</li>
        <li>small interface preferences, such as where you dragged a panel.</li>
      </ul>
      <p>
        You can clear all of it at any time in your browser settings; you will simply be signed out and lose any
        unfinished draft. If you sign in with Google, Google sets its own cookies on its own website, governed by
        Google&rsquo;s privacy policy rather than this one.
      </p>

      <h2>Security</h2>
      <p>
        Connections are encrypted, access to our systems is restricted and authenticated, and passwords are handled
        only by our sign-in provider. No system is perfectly secure. If we learn of a breach that affects you, we
        will tell you and the regulator as the law requires.
      </p>

      <h2>Children</h2>
      <p>{name} is for adults and is not intended for anyone under 18.</p>

      <h2>Changes</h2>
      <p>
        If we change this policy, we will update the date above. If a change is significant, we will tell you by
        email or in the app before it takes effect.
      </p>

      <h2>Contact</h2>
      <p>
        {brand.legal_name}, {support}
      </p>
    </LegalPage>
  );
}

export function TermsPage() {
  const { brand } = useBranding();
  const name = brand.product_name;
  const support = <Mail to={brand.support_email} />;
  return (
    <LegalPage title="Terms of Service">
      <p>
        These terms apply when you use {name} at {brand.primary_domain}. {name} is run by {brand.legal_name}{" "}
        (“we”, “us”). By creating an account or using {name}, you agree to them. If you use {name} for an
        organisation, you agree on its behalf and confirm that you are allowed to.
      </p>

      <h2>What {name} does and does not do</h2>
      <p>
        {name} simulates how an audience might react to a message. It uses AI to create personas, has them respond
        to your message and to each other, and summarises what happened.
      </p>
      <p>
        <strong>The results are simulations, not the views of real people.</strong> {name} is an experimental tool
        for exploring how messages might land. It is not a validated research instrument, and we make no claim that
        it predicts real-world opinion. Results can be wrong, incomplete or biased, and can vary between runs. Use
        them as one input alongside your own judgement, never as your only basis for important decisions. Nothing
        in {name} is legal, financial, medical or other professional advice.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>You must be at least 18 years old.</li>
        <li>
          Keep your sign-in secure. You are responsible for what happens in your account, and should tell us
          promptly at {support} if you think someone else has used it.
        </li>
        <li>
          If you are invited to an organisation’s workspace, its owners can see and manage the studies in it.
        </li>
      </ul>

      <h2>Credits and plans</h2>
      <p>
        Running studies uses credits. The processing a study uses is charged against your credit balance, and
        {" "}{name} shows the estimated cost before you run it. New accounts receive a one-time trial credit with
        limited features. Trial credit has no cash value, cannot be transferred and may be withdrawn if it is
        misused. During early access, live studies are available by invitation only.
      </p>
      <p>
        Paid plans are monthly subscriptions, priced in US dollars. The price, the credit a plan includes and
        what it allows are shown on the billing page before you buy. A plan renews each month until you cancel,
        and each renewal includes that month&rsquo;s credit. Credit included with a plan expires at the end of the
        month it was granted for &mdash; it does not roll over. Credit bought as a one-off top-up does not expire.
        We never charge you for usage beyond credit you already hold: when it runs out, studies stop until you
        top up or change plan.
      </p>
      <p>
        You can change or cancel a plan whenever you like from the billing page, which opens Stripe&rsquo;s billing
        portal. Cancelling stops the next renewal; your plan and its remaining credit run to the end of the period
        you have paid for, and part months are not refunded. If a payment fails, live studies pause and your
        studies and results stay exactly as they are until the card is sorted out. {brand.legal_name} is not
        registered for VAT, so no VAT is charged. Payments are taken by Stripe; card details never reach us.
        Nothing here affects your legal rights as a consumer, including any right to cancel a recent purchase.
      </p>

      <h2>Your content</h2>
      <p>
        You keep ownership of what you put into {name} and of the results it gives you. You give us permission to
        store and process that content only as needed to run {name} for you, including sending it to the AI
        providers described in our <Link to="/privacy">Privacy Policy</Link>. You must have the right to use
        anything you enter, and you must not enter personal data about other people unless you have a lawful basis
        to do so. AI can produce similar results for other users, so results are not unique to you.
      </p>

      <h2>Acceptable use</h2>
      <p>You must not use {name} to:</p>
      <ul>
        <li>break the law or infringe anyone’s rights;</li>
        <li>
          create content that is fraudulent, harassing or hateful, or content meant to deceive people, such as
          disinformation or impersonation;
        </li>
        <li>
          do anything that Anthropic’s{" "}
          <a href="https://www.anthropic.com/legal/aup">Usage Policy</a> prohibits, because {name} relies on
          Anthropic’s models;
        </li>
        <li>
          try to get around limits or security, access other people’s data, or overload, scrape or reverse-engineer
          the service;
        </li>
        <li>resell or give others access to {name} without our written permission.</li>
      </ul>

      <h2>Availability and changes</h2>
      <p>
        {name} is at an early stage. We work to keep it running well, but we do not promise it will always be
        available or free of errors. We may change or remove features. If a change significantly reduces what you
        have paid for, we will tell you in advance where we reasonably can.
      </p>

      <h2>Ending your use</h2>
      <p>
        You can stop using {name} at any time and ask us to delete your account at {support}. We may suspend or close
        an account that breaks these terms or puts the service or other users at risk. Where appropriate, we will
        tell you why first. The sections on your content, liability and the law continue to apply after your
        account closes.
      </p>

      <h2>Liability</h2>
      <p>
        Nothing in these terms limits liability for death or personal injury caused by negligence, for fraud, or
        for anything else the law does not allow us to limit.
      </p>
      <p>
        <strong>If you use {name} as a consumer</strong>, we are responsible for loss or damage you suffer that is
        a foreseeable result of our breaking these terms or failing to use reasonable care and skill. We are not
        responsible for business losses, because consumers use {name} for private purposes.
      </p>
      <p>
        <strong>If you use {name} for business</strong>, the service is provided “as is”, to the extent the law
        allows. We are not liable for indirect or consequential loss, or for loss of profit, revenue, business,
        goodwill or data. Our total liability to you in any 12-month period is limited to the greater of what you
        paid us in that period and £100.
      </p>
      <p>
        You are responsible for the decisions you make using {name}’s results (see “What {name} does and does not
        do” above).
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We may update these terms. If a change is significant, we will tell you by email or in the app before it
        takes effect. If you keep using {name} after that, the new terms apply. If you do not agree, you can stop
        using {name} and close your account.
      </p>

      <h2>Law and disputes</h2>
      <p>
        These terms are governed by the law of {brand.legal_jurisdiction}, and its courts will hear any dispute.
        If you are a consumer living elsewhere in the UK or the EU, you keep the protection of your local law and
        may bring claims in your local courts. Please contact us first at {support}: most problems can be sorted
        out quickly.
      </p>

      <h2>Contact</h2>
      <p>
        {brand.legal_name}, {support}
      </p>
    </LegalPage>
  );
}
